import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, FormControl } from '@angular/forms';
import { PointOfInterest } from '../categories/categories.model';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { NodeService } from '../../services/node-service/node-service.service';
import { RootcategoriesService } from '../rootcategories/rootcategories.service';
import { PointsOfInterestService } from '../points-of-interest/points-of-interest.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MapInfoWindow } from '@angular/google-maps';

const IS_PARENT_CATEGORY = 'isParentCategory';
const VIEW_TITLE = 'viewTitle';
const PARENT_ID_VALUE = 'parentIdValue';
const MIN_LENGTH_NAME = 3;
const STATUS_OK = 201;
const STATUS_OK_2 = 200;
const SNACKBAR_DURATION = 4000;

@Component({
  selector: 'app-add-poi',
  templateUrl: './add-poi.component.html',
  styleUrls: ['./add-poi.component.css'],
  standalone: false,
})
export class AddPoiComponent implements OnInit, OnDestroy {
  @Input(IS_PARENT_CATEGORY) isParentCategory: boolean = true;
  @Input(VIEW_TITLE) viewTitle: string = 'Agregar punto de interés';
  @Input(PARENT_ID_VALUE) parentIdValue: number = 0;
  @ViewChildren(MapInfoWindow) infoWindowsView?: QueryList<MapInfoWindow>;

  center = { lat: 0, lng: 0 };
  zoom = 16;
  display?: google.maps.LatLngLiteral;
  pois: google.maps.LatLngLiteral[] = [];
  poisList: PointOfInterest[] = [];

  markerOptions: google.maps.MarkerOptions = {
    draggable: true,
  };

  mapOptions: google.maps.MapOptions = {
    disableDefaultUI: false,
  };

  poiForm!: FormGroup;
  subscription!: Subscription;
  subscriptionService!: Subscription;
  subscriptionBulkService: Subscription | undefined;
  defaulValues: PointOfInterest = {
    id: 0,
    lat: 0,
    lng: 0,
  };
  loadCsv: boolean = true;
  disabledSendBulkButton: boolean = true;
  showForm: boolean = true;
  fileName: string = '.xlsx, .xls';
  formData!: FormData;
  isLoading: boolean = false;
  isEditable: boolean = false;

  telefonos: FormArray = this.formBuilder.array([]);
  paginaWeb: FormControl = this.formBuilder.control('');
  email: FormControl = this.formBuilder.control('');
  especialidades: FormArray = this.formBuilder.array([]);
  horarios: FormArray = this.formBuilder.array([]);
  emailPattern = '^[a-z0-9._%+\\-]+@[a-z0-9.\\-]+\\.[a-z]{2,4}$';
  maxFileSize = 1 * 1024 * 1024;

  constructor(
    private formBuilder: FormBuilder,
    public route: ActivatedRoute,
    private nodeService: NodeService,
    private rootcategoriesService: RootcategoriesService,
    private pointsOfInterestService: PointsOfInterestService,
    public router: Router,
    private snackbar: MatSnackBar
  ) {}

  /**
   * * Loads the form with the POI data to edit
   */
  ngOnInit(): void {
    this.subscription = this.route.url.subscribe((url) => {
      const result = url.filter((item) => item.path === 'edit');
      if (result.length > 0) {
        this.loadCsv = false;
        this.isParentCategory = false;

        const poiToEdit = this.nodeService.getPoiToEdit();

        this.isEditable = poiToEdit?.id === 0 ? false : true;

        if (this.isEditable) {
          this.poiForm = this.loadPoiForm(poiToEdit);
          return this.poiForm;
        }

        this.showForm = false;
        this.rootcategoriesService.openSnacBar(
          'Para editar un punto de interés, debe seleccionarlo desde su vista'
        );
      }

      this.defaulValues.id = this.parentIdValue;

      this.poiForm = this.loadPoiForm(this.defaulValues);
      return this.poiForm;
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.subscriptionService?.unsubscribe();
  }

  /**
   * Loads the POI form with the data of the POI that is being edited
   * @param {PointOfInterest} data - PointOfInterest
   * @returns The form group.
   */
  loadPoiForm(data: PointOfInterest) {
    this.center = { lat: data.lat!, lng: data.lng! };
    this.poisList.push(data);
    this.createDynamics(data);

    return this.formBuilder.group({
      categoryId: [
        { value: data?.id, disabled: true },
        [Validators.required, Validators.minLength(1), Validators.pattern('[0-9]')],
      ],
      nombre: [data?.nombre, [Validators.required, Validators.minLength(MIN_LENGTH_NAME)]],
      direccionCompleta: [data?.direccionCompleta, Validators.minLength(1)],
      tipoVia: [data?.tipoVia, Validators.minLength(1)],
      calle: [data?.calle, Validators.minLength(1)],
      numero: [data?.numero, Validators.minLength(1)],
      comuna: [data?.comuna, Validators.minLength(1)],
      ciudad: [data?.ciudad, Validators.minLength(1)],
      fax: [data?.fax, Validators.minLength(1)],
      web: [data?.web, Validators.minLength(1)],
      email: [data?.email],
      observacion: [data?.observacion, Validators.minLength(1)],
      ubicacion: [data?.ubicacion, Validators.minLength(1)],
      lat: [data?.lat, [Validators.required, Validators.minLength(1)]],
      lng: [data?.lng, [Validators.required, Validators.minLength(1)]],
      razonSocial: [data?.razonSocial, Validators.minLength(1)],
      rutRuc: [data?.rutRuc, Validators.minLength(1)],
      urlFuente: [data?.urlFuente, Validators.minLength(1)],
      webFuente: [data?.webFuente, Validators.minLength(1)],
      observacionesInternas: [data?.observacionesInternas, Validators.minLength(1)],
      region: [data?.region, Validators.minLength(1)],
      local: [data?.local, Validators.minLength(1)],
      especialidad: [data?.especialidad, Validators.minLength(1)],
    });
  }

  /**
   * *Create the dynamics fields for the contact information and the speciality hours.*
   *
   * The function is pretty simple. It checks if the `otrosDatos` field is not null and if it is not, it
   * parses the JSON string and checks if the `datos_contacto` field is present. If it is, it creates the
   * fields for the contact information and the speciality hours
   * @param {PointOfInterest} data - PointOfInterest
   */
  createDynamics(data: PointOfInterest): void {
    if (data.otrosDatos) {
      let jsonOtrosDatos = JSON.parse(data.otrosDatos as string);
      if ('datos_contacto' in jsonOtrosDatos) {
        this.addFonoEspecialidadHorario(jsonOtrosDatos);
        this.addWebAndEmail(jsonOtrosDatos);
      }
    }
  }

  /**
   * * Add the web and email fields to the form if they exist in the fixedData object
   * @param {any} fixedData - any
   */
  addWebAndEmail(fixedData: any): void {
    if ('web' in fixedData.datos_contacto) {
      this.paginaWeb.setValue(fixedData.datos_contacto.web);
    }
    if ('email' in fixedData.datos_contacto) {
      this.email.setValue(fixedData.datos_contacto.email);
    }
  }

  /**
   * Add the dynamic data to the appropriate array
   * @param {any} dynamicData - any
   */
  addFonoEspecialidadHorario(dynamicData: any): void {
    if ('telefonos' in dynamicData.datos_contacto) {
      dynamicData.datos_contacto.telefonos.forEach((telefono: string) =>
        this.addDynamic(this.telefonos, telefono)
      );
    }
    if ('especialidades' in dynamicData) {
      dynamicData.especialidades.forEach((especialidad: string) =>
        this.addDynamic(this.especialidades, especialidad)
      );
    }
    if ('horarios' in dynamicData) {
      dynamicData.horarios.forEach((horarios: string) => this.addDynamic(this.horarios, horarios));
    }
  }

  addDynamic(formArray: FormArray, defaulText: string): void {
    formArray.push(this.formBuilder.control(defaulText));
  }

  getDynamicByIndex(formArray: FormArray, index: number): FormControl {
    return formArray.at(index) as FormControl;
  }

  deleteDynamic(formArray: FormArray, index: number): void {
    formArray.removeAt(index);
  }

  deleteAllDynamic(formArray: FormArray): void {
    formArray.clear();
  }

  /**
   * Move the element at the given index up one position in the array
   * @param {FormArray} formArray - The FormArray that contains the FormGroup that you want to move.
   * @param {number} index - The index of the control to move.
   */
  moveUp(formArray: FormArray, index: number) {
    if (index >= formArray['controls'].length) {
      throw new Error('El índice no debe ser mayor que el largo de control');
    }

    let tmp = formArray.at(index);
    this.removeDynamic(formArray, index);
    formArray.insert(index - 1, tmp);
  }

  /**
   * Move the element at the given index down one position in the array
   * @param {FormArray} formArray - The FormArray that contains the FormGroup that you want to move.
   * @param {number} index - The index of the item to be moved.
   */
  moveDown(formArray: FormArray, index: number) {
    if (index < 0) {
      throw new Error('El índice no debe ser menor que 0');
    }

    let tmp = formArray.at(index);
    this.removeDynamic(formArray, index);
    formArray.insert(index + 1, tmp);
  }

  removeDynamic(formArray: FormArray, index: number) {
    formArray.removeAt(index);
  }

  /**
   * Create a JSON object with the otros_datos field
   * @returns The JSON string of the data that will be sent to the API.
   */
  createOtrosDatos(): string {
    let jsonOtrosDatos: any = {
      datos_contacto: {},
    };

    let especialidades = this.especialidades.controls.map(
      (especialidadControl) => especialidadControl.value
    );
    let telefonos = this.telefonos.controls.map((telefonoControl) => telefonoControl.value);
    let horarios = this.horarios.controls.map((horariosControl) => horariosControl.value);

    jsonOtrosDatos.especialidades = especialidades;
    jsonOtrosDatos.horarios = horarios;
    jsonOtrosDatos.datos_contacto = {
      telefonos: telefonos,
      web: this.paginaWeb.value,
      email: this.email.value,
    };

    return JSON.stringify(jsonOtrosDatos);
  }

  /**
   * If the form is editable, we'll call the editPois() function of the pointsOfInterestService. If it's
   * not editable, we'll call the addPois() function
   */
  save(): void {
    if (this.isEditable) {
      this.pointsOfInterestService.editPois([this.formToPois()]).subscribe((response) => {
        this.handleRedirect(response);
      });
    } else {
      this.pointsOfInterestService.addPois([this.formToPois()]).subscribe((response) => {
        this.handleRedirect(response);
      });
    }
  }

  /**
   * If the response status is 200, the new point of interest was created successfully. If the response
   * status is 201, the point of interest was edited successfully. Otherwise, the point of interest was
   * not created or edited
   * @param {any} response - The response from the server.
   * @returns The status code.
   */
  handleRedirect(response: any) {
    if (response.status === STATUS_OK_2) {
      this.openSnackBar('El nuevo punto de interés fue creado con éxito');

      this.router.routeReuseStrategy.shouldReuseRoute = function () {
        return false;
      };

      if (this.isEditable) {
        this.router.navigate(['category', this.nodeService.getPoiToEdit().categoryId]);
      } else {
        this.router.navigate(['category', this.parentIdValue]);
      }
    } else if (response.status === STATUS_OK) {
      this.openSnackBar('El punto de interés fue editado con éxito');

      this.router.routeReuseStrategy.shouldReuseRoute = function () {
        return false;
      };

      if (this.isEditable) {
        this.router.navigate(['category', this.nodeService.getPoiToEdit().categoryId]);
      } else {
        this.router.navigate(['category', this.parentIdValue]);
      }
    } else if (response.status === 400) {
      this.openSnackBar(response.body);
    } else {
      this.openSnackBar('El nuevo punto de interés no pudo ser creado');
    }
  }

  openSnackBar(message: string): void {
    this.snackbar.open(`${message}`, 'X', {
      duration: SNACKBAR_DURATION,
    });
  }

  /**
   * *This function is called when the user selects a file to upload. It creates a new FormData object
   * and adds the file to it. It also sets the fileName property to the name of the file
   * @param {any} evt - any
   * @returns The file is being returned to the server.
   */
  onFileChange(evt: any) {
    const target: DataTransfer = <DataTransfer>evt.target;

    if (target.files.length !== 1) {
      return this.rootcategoriesService.openSnacBar('Seleccione un archivo');
    }

    const file: File = target.files[0];

    if (file.size > this.maxFileSize) {
      this.resetBulk();
      return this.rootcategoriesService.openSnacBar('El archivo excede el máximo tamaño permitido');
    } else {
      this.disabledSendBulkButton = false;

      this.fileName = file.name;

      this.formData = new FormData();

      this.formData.append('file', file, file.name);
    }
  }

  /**
   * Send the file to the bulk endpoint
   */
  sendFileToBulk() {
    this.isLoading = true;
    this.disabledSendBulkButton = false;
    this.subscriptionService = this.pointsOfInterestService
      .bulkPois(this.defaulValues.id, this.formData)
      .subscribe({
        next: (response) => {
          this.disabledSendBulkButton = true;
          this.rootcategoriesService.openSnacBar(response.message);
          this.rootcategoriesService.openSnacBar(
            'Aguarde un instante mientras procesamos tus puntos de interés.'
          );
          this.router.navigate(['/', 'category', this.parentIdValue]);
        },
        error: (error) => this.rootcategoriesService.openSnacBar('Ha ocurrido un error'),
      });
  }

  /**
   * Reset the form data and the file name
   */
  resetBulk(): void {
    this.formData.delete('file');
    this.fileName = '.xlsx, .xls';
    this.isLoading = false;
    this.formData = new FormData();
    this.disabledSendBulkButton = true;
  }

  /**
   * Create a POI object from the form values
   * @returns The POI object.
   */
  formToPois(): any {
    let pois = {
      nombre: this.poiForm.get('nombre')?.value,
      direccionCompleta: this.poiForm.get('direccionCompleta')?.value,
      tipoVia: this.poiForm.get('tipoVia')?.value,
      calle: null,
      numero: null,
      comuna: this.poiForm.get('comuna')?.value,
      ciudad: this.poiForm.get('ciudad')?.value,
      telefono: null,
      telefono2: null,
      fax: this.poiForm.get('fax')?.value,
      web: this.poiForm.get('web')?.value,
      email: this.poiForm.get('email')?.value,
      observacion: this.poiForm.get('observacion')?.value,
      ubicacion: this.poiForm.get('ubicacion')?.value,
      cantidad: null,
      lat: Number(this.poiForm.get('lat')?.value),
      lng: Number(this.poiForm.get('lng')?.value),
      categoryId: Number(this.poiForm.get('categoryId')?.value),
      categoriesHash: null,
      idDireccion: null,
      idOrigenDivisionPoliticaN3: null,
      fuente: null,
      idFuente: null,
      estado: null,
      razonSocial: this.poiForm.get('razonSocial')?.value,
      rutRuc: this.poiForm.get('rutRuc')?.value,
      urlFuente: this.poiForm.get('urlFuente')?.value,
      webFuente: this.poiForm.get('webFuente')?.value,
      observacionesInternas: this.poiForm.get('observacionesInternas')?.value,
      divisionPoliticaNivel3Id: null,
      regionN: null,
      region: this.poiForm.get('region')?.value,
      local: null,
      especialidad: null,
      categoriesIds: [],
      otrosDatos: this.createOtrosDatos(),
    };

    if (this.isEditable) {
      let id = this.nodeService.getPoiToEdit().id;
      let categoryId = this.nodeService.getPoiToEdit().categoryId;
      Object.assign(pois, { id: id, categoryId: categoryId });
    } else {
      let categoryId = this.parentIdValue;
      Object.assign(pois, { categoryId: categoryId });
    }

    return pois;
  }

  /**
   * When the user drags the marker, the form is updated with the new coordinates
   * @param event - The event object.
   */
  onDragendHandler(event: google.maps.MapMouseEvent) {
    this.poiForm.patchValue({
      lat: event.latLng!.lat(),
      lng: event.latLng!.lng(),
    });

    this.center = {
      lat: event.latLng!.lat(),
      lng: event.latLng!.lng(),
    };
  }
}
